import PoolMatchCard from './PoolMatchCard'
import SundayFinalsResultCard from './SundayFinalsResultCard'
import useBracketStore from '@/stores/useBracketStore'
import { scrollToPoolMatch } from '@/lib/poolScroll'

/**
 * Sunday cross-pool matchups (4 matches paired by predicted pool rank:
 * A1 vs B1, A2 vs B2, A3 vs B3, A4 vs B4). Participants are derived from the
 * user's pool picks; if pool picks are incomplete or seeding is ambiguous,
 * the slots show TBD and the winner buttons are disabled.
 */
export default function SundayMiniBracket() {
  const poolStructure = useBracketStore(s => s.poolStructure)
  const picks = useBracketStore(s => s.picks)
  const bracket = useBracketStore(s => s.bracket)
  const canEditPicks = useBracketStore(s => s.canEditPicks)
  const isViewingOfficial = useBracketStore(s => s.isViewingOfficial)
  const handleSelectWinner = useBracketStore(s => s.handleSelectWinner)

  if (!poolStructure?.sunday) return null

  const teamSize = bracket?.divisionTeamSize || 1

  // Reason the Finals slots are still locked, for a clearer placeholder than
  // a blanket "Make pool picks" (DUB-140): distinguish incomplete pools from
  // an unresolved tiebreaker.
  const pools = poolStructure.pools || []
  const allPicksDone = pools.length > 0 && pools.every(p => p.picksDone)
  const lockReason = allPicksDone ? 'Choose who advances' : 'Make pool picks'

  return (
    <section className="rounded-lg border bg-background p-4 space-y-3">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Finals</h2>
        <span className="text-xs text-muted-foreground">
          Pairs by predicted pool rank
        </span>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {poolStructure.sunday.map(match => {
          const pick = picks[match.match_id]
          // Tag teams with the synthetic seed = sunday_seed_index for display.
          const teamA = match.teamA ? { ...match.teamA, seed: match.sunday_seed_index } : null
          const teamB = match.teamB ? { ...match.teamB, seed: match.sunday_seed_index } : null
          const placeholder = match.isReady ? null : lockReason

          // Comparison view: as soon as the admin sets the official Finals
          // matchups (advancers chosen), show the overlay comparing the user's
          // predicted finalists against the actual ones — don't wait for the
          // matches to be scored (DUB-186). The winner highlight fills in later,
          // once an official winner is posted.
          const officialWinnerId = !isViewingOfficial ? (pick?.winner_team_id || null) : null
          const showResultOverlay = !canEditPicks && !!(match.officialTeamA || match.officialTeamB)

          const header = (
            <div className="text-xs uppercase tracking-wide text-muted-foreground text-center">
              Match {match.sunday_seed_index} — A{match.sunday_seed_index} vs B{match.sunday_seed_index}
            </div>
          )

          // Results view: wrap each matchup in its own subtle frame; the two
          // inner cards (Actual Final / Your Prediction) each rise on hover,
          // mirroring the pool-play match cards. (The editable PoolMatchCard
          // already carries its own border + hover lift.)
          return (
            <div key={match.match_id}>
              {showResultOverlay ? (
                <div className="rounded-lg border bg-background p-2.5 space-y-1.5">
                  {header}
                  <SundayFinalsResultCard
                    userTeamA={teamA}
                    userTeamB={teamB}
                    officialTeamA={match.officialTeamA ? { ...match.officialTeamA, seed: match.sunday_seed_index } : null}
                    officialTeamB={match.officialTeamB ? { ...match.officialTeamB, seed: match.sunday_seed_index } : null}
                    userWinnerPickId={pick?.teamId || null}
                    officialWinnerId={officialWinnerId}
                    teamSize={teamSize}
                  />
                </div>
              ) : (
                <div className="space-y-1">
                  {header}
                  <PoolMatchCard
                    match={match}
                    teamOne={teamA}
                    teamTwo={teamB}
                    teamSize={teamSize}
                    pick={pick}
                    canEdit={canEditPicks && match.isReady}
                    isViewingOfficial={isViewingOfficial}
                    onSelectWinner={(m, teamId) => handleSelectWinner(m, teamId, scrollToPoolMatch)}
                    placeholder={placeholder}
                    isChampionPick
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
